<script lang="ts">
    import { api, appconfig } from '@cwd/lib/main';
    import { dateFormat } from '@cwd/shared/utils';
    import { computed, ComputedRef, inject, onBeforeMount, onMounted, reactive, Ref, ref, watch } from 'vue';

    import getProps, { CronModes, CronMode } from './CronProperties';

    // components
    import FluidButton from '@shared/components/FluidButton.vue';
    import FluidToggle from '@cwd/shared/components/FluidToggle.vue';
    import DateTimePicker, { PickerType } from '@shared/components/DateTimePicker/DateTimePicker.vue';
    import NativeSelect from '@shared/components/NativeSelect.vue';

    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import InputCheckbox from '@shared/components/InputCheckbox.vue';

    // utils
    import { debounce } from '@shared/utils';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { ContextAccess } from '@shared/Root/Context.access';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'CronEditor',
    };
    export default {
        name: component.name,
    };
</script>

<script setup lang="ts">
    const props = defineProps<{
        cronExpression: any;
        nextDates: any;
        selectedStart: any;
        timeZone: any;
    }>();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const emit = defineEmits<{
        (ev: 'update:cronExpression', value: string): void;
        (ev: 'update:timeZone', value: string): void;
        (ev: 'update:nextDates', value: string[]): void;
        (ev: 'update:selectedStart', value: string): void;
    }>();

    interface CronEditorState {
        loaded: boolean;
        nextDates: string[];
        timeZones: any;
    }

    const { cronValues, modes } = getProps(),
        state: CronEditorState = reactive({
            loaded: false,
            nextDates: props.nextDates.value || [],
            timeZones: [],
        });

    // **
    const selected = reactive({
        start: props.selectedStart || dateFormat('mm/dd/yyyy'),
        hourlyMode: 'hour',
        dailyMode: 'day',
        weekdays: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
        monthlyMode: 'day',
        yearlyMode: 'day',
        mode: 'daily',
        minute: 0,
        hour: 0,
        day: 1,
        weekday: 'MON',
        dayOfMonth: '1',
        month: 'JAN',
        months: ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'],
        dayOrderInMonth: '#1',
        timeZone: props.timeZone,
    });

    watch(selected, (val: any) => {
        cronExpression = getCronExpression(val);
        debounce(() => getNextDates(cronExpression));
    });

    onMounted(() => {
        identifyMode(props.cronExpression);
        getTimeZones();
        getNextDates(props.cronExpression || cronExpression);
    });

    const emitUpdates = () => {
        let ce = getCronExpression(selected);
        emit('update:cronExpression', ce);
        emit('update:timeZone', selected.timeZone);
        emit('update:nextDates', state.nextDates);
        emit('update:selectedStart', new Date(selected.start).toISOString());
    };

    const getTimeZones = () => {
        api.get('/timezones', null, state.timeZones);
    };

    const getNextDates = (cronExpr: string = cronExpression) => {
        if (!cronExpr) return;

        let langId = appconfig.lang.loc; // value from from ko UserVM.. AccountVM? I have no memory
        if (langId == `no`) {
            api.get(
                '/CronExpressions/GetFutureDates?cronExpression=' +
                    encodeURIComponent(cronExpression) +
                    '&timeZone=' +
                    selected.timeZone +
                    '&count=5&start=' +
                    encodeURIComponent(dateFormat('yyyy-mm-dd', selected.start)),
                null,
                state.nextDates
            ).then(() => emitUpdates());
        } else {
            api.get(
                '/CronExpressions/GetFutureDates?cronExpression=' +
                    encodeURIComponent(cronExpression) +
                    '&timeZone=' +
                    selected.timeZone +
                    '&count=5&start=' +
                    encodeURIComponent(selected.start),
                null,
                state.nextDates
            ).then(() => emitUpdates());
        }
    };

    const getCronExpression = ({
        start,
        dailyMode,
        weekdays,
        monthlyMode,
        yearlyMode,
        mode,
        minute,
        hour,
        day,
        weekday,
        dayOfMonth,
        month,
        months,
        dayOrderInMonth,
    }: typeof selected) => {
        switch (mode) {
            case 'minutes':
                return `0 0/${minute} 0/1 1/1 * ? *`;

            case 'hourly':
                return `0 ${minute} 0/${hour} 1/1 * ? *`;

            case 'daily':
                if (dailyMode == 'day') return `0 ${minute} ${hour} 1/1 * ? *`;
                else if (dailyMode == 'odd') return `0 ${minute} ${hour} 1-31/2 * ? *`;
                else if (dailyMode == 'even') return `0 ${minute} ${hour} 2-30/2 * ? *`;

            case 'weekly':
                return `0 ${minute} ${hour} ? * ${weekdays.join(',')} *`;

            case 'monthly':
                if (monthlyMode == 'day') return `0 ${minute} ${hour} ${dayOfMonth} ${months.length == 0 ? '*' : months.join(',')} ? *`;
                else return `0 ${minute} ${hour} ? ${months.length == 0 ? '*' : months.join(',')} ${weekday + dayOrderInMonth} *`;

            case 'yearly':
                if (yearlyMode == 'day') return `0 ${minute} ${hour} ${dayOfMonth} ${month} ? *`;
                else return `0 ${minute} ${hour} ? ${month} ${weekday + dayOrderInMonth} *`;

            default:
                return props.cronExpression;
        }
    };

    const identifyMode = (cronExpression: string) => {
        var res = cronExpression.match(/0 0\/(\d+) 0\/1 1\/1 \* \? \*/);
        if (res?.length) {
            selected.mode = 'minutes';
            selected.minute = parseInt(res[1]);
            return;
        }

        res = cronExpression.match(/0 (\d+) 0\/(\d+) 1\/1 \* \? \*/);
        if (res?.length) {
            selected.mode = 'hourly';
            selected.minute = parseInt(res[1]);
            selected.hour = parseInt(res[2]);
            return;
        }

        res = cronExpression.match(/0 (\d+) (\d+) 1\/1 \* \? \*/);
        if (res?.length) {
            selected.mode = 'daily';
            selected.minute = parseInt(res[1]);
            selected.hour = parseInt(res[2]);
            selected.dailyMode = 'day';
            return;
        }

        res = cronExpression.match(/0 (\d+) (\d+) 1-31\/2 \* \? \*/);
        if (res?.length) {
            selected.mode = 'daily';
            selected.minute = parseInt(res[1]);
            selected.hour = parseInt(res[2]);
            selected.dailyMode = 'odd';
            return;
        }

        res = cronExpression.match(/0 (\d+) (\d+) 2-30\/2 \* \? \*/);
        if (res?.length) {
            selected.mode = 'daily';
            selected.minute = parseInt(res[1]);
            selected.hour = parseInt(res[2]);
            selected.dailyMode = 'even';
            return;
        }

        res = cronExpression.match(/0 (\d+) (\d+) \? \* ((MON|TUE|WED|THU|FRI|SAT|SUN|\,)+) \*/);
        if (res?.length) {
            selected.mode = 'weekly';
            selected.minute = parseInt(res[1]);
            selected.hour = parseInt(res[2]);
            selected.weekdays = res[3].split(',');
            return;
        }

        res = cronExpression.match(/0 (\d+) (\d+) \? (JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC) (MON|TUE|WED|THU|FRI|SAT|SUN)((\#\d)|L) */);
        if (res?.length) {
            selected.mode = 'yearly';
            selected.minute = parseInt(res[1]);
            selected.hour = parseInt(res[2]);
            selected.month = res[3];
            selected.months = [res[3]];
            selected.weekday = res[4];
            selected.dayOrderInMonth = res[6];
            selected.yearlyMode = 'weekday';
            return;
        }

        res = cronExpression.match(/0 (\d+) (\d+) (\d+) (JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC) \? */);
        if (res?.length) {
            selected.mode = 'yearly';
            selected.minute = parseInt(res[1]);
            selected.hour = parseInt(res[2]);
            selected.dayOfMonth = parseInt(res[3]);
            selected.month = res[4];
            selected.months = [res[4]];
            selected.yearlyMode = 'day';
            return;
        }

        res = cronExpression.match(/0 (\d+) (\d+) (\d+W|\d+|L|LW) (\*|(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC|\,)+) \? \*/);
        if (res?.length) {
            selected.mode = 'monthly';
            selected.minute = parseInt(res[1]);
            selected.hour = parseInt(res[2]);
            selected.dayOfMonth = res[3];
            selected.months = res[4].split(',');
            selected.monthlyMode = 'day';
            return;
        }

        res = cronExpression.match(/0 (\d+) (\d+) \? (\*|(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC|\,)+) (MON|TUE|WED|THU|FRI|SAT|SUN)((\#\d)|L) \*/);
        if (res?.length) {
            selected.mode = 'monthly';
            selected.minute = parseInt(res[1]);
            selected.hour = parseInt(res[2]);
            selected.months = res[3].split(',');
            selected.weekday = res[5];
            selected.dayOrderInMonth = res[6];
            selected.monthlyMode = 'weekday';
            return;
        }

        return null;
    };

    let cronExpression = getCronExpression(selected),
        weak_prop: any;

    const currentTab = computed(() => {
        if (selected.mode === 'daily') return 1;
        if (selected.mode === 'weekly') return 2;
        if (selected.mode === 'monthly') return 3;
        if (selected.mode === 'yearly') return 4;

        return 0;
    });

    const checkboxToggle = (add: boolean, val: string, arr: string[]) => {
        add ? arr.push(val) : arr.splice(arr.indexOf(val), 1);
    };
</script>

<template>
    <tabs navClass="tabs list-unstyled d-flex flex-justified text-center" :selectedIndex="currentTab">
        <tab @tabClick="selected.mode = 'hourly'" title="(Admin)" v-if="featureAccess.hasFeature('BackOffice')">
            <div class="form-group">
                <FluidToggle
                    v-for="{ type, lang_rsc } of modes.hourly"
                    :key="selected.mode + type"
                    type="radio"
                    class="slide rounded m-sm-t"
                    v-model:value="selected.mode"
                    :checked="type"
                    :text="lang_rsc" />
            </div>

            <div class="form-group">
                <label>{{ $Resources.CronAtLabel }}:</label>
                <DateTimePicker type="time" v-model:value_hour="selected.hour" v-model:value_minute="selected.minute" />
            </div>
        </tab>
        <tab @tabClick="selected.mode = 'daily'" :title="$Resources.CronDailyTab">
            <div class="form-group">
                <FluidToggle
                    v-for="{ type, lang_rsc } of modes.daily"
                    :key="selected.mode + type"
                    type="radio"
                    class="slide rounded m-sm-t"
                    v-model:value="selected.dailyMode"
                    :checked="type"
                    :text="lang_rsc" />
            </div>

            <div class="form-group">
                <label>{{ $Resources.CronAtLabel }}:</label>
                <DateTimePicker type="time" v-model:value_hour="selected.hour" v-model:value_minute="selected.minute" />
            </div>
        </tab>
        <tab @tabClick="selected.mode = 'weekly'" :title="$Resources.CronWeeklyTab">
            <h3 class="h4">{{ $Resources.CronEveryLabel }}:</h3>

            <div class="form-group d-flex flex-wrap">
                <InputCheckbox
                    style="width: 33.333%"
                    v-for="({ value, lang_rsc }, idx) of cronValues.weekdays"
                    :checked="selected.weekdays.includes(value)"
                    @update:checked="(newValue) => checkboxToggle(newValue, value, selected.weekdays)">
                    <template #label>
                        {{ lang_rsc }}
                    </template>
                </InputCheckbox>
            </div>

            <div class="form-group">
                <label>{{ $Resources.CronAtLabel }}:</label>
                <DateTimePicker type="time" v-model:value_hour="selected.hour" v-model:value_minute="selected.minute" />
            </div>
        </tab>
        <tab @tabClick="selected.mode = 'monthly'" :title="$Resources.CronMonthlyTab">
            <div class="form-group d-flex gap-lg">
                <FluidToggle
                    type="radio"
                    class="slide rounded"
                    v-model:value="selected.monthlyMode"
                    :checked="modes.monthly[0].type"
                    :text="$Resources.CronOnTheLabel + ':'" />

                <NativeSelect v-model:value="selected.dayOfMonth" class="fit-cw" :class="selected.monthlyMode !== modes.monthly[0].type && 'inactive'">
                    <template v-slot:options>
                        <option v-for="{ value, lang_rsc } of cronValues.daysOfMonth" :key="selected.mode + value" :value="value">{{ lang_rsc }}</option>
                    </template>
                </NativeSelect>
            </div>

            <div class="form-group d-flex gap-lg">
                <FluidToggle
                    type="radio"
                    class="slide rounded"
                    v-model:value="selected.monthlyMode"
                    :checked="modes.monthly[1].type"
                    :text="$Resources.CronOnTheLabel + ':'" />

                <NativeSelect v-model:value="selected.dayOrderInMonth" :class="selected.monthlyMode !== modes.monthly[1].type && 'inactive'">
                    <template v-slot:options>
                        <option v-for="{ value, lang_rsc } of cronValues.dayOrdersInMonth" :key="selected.mode + value" :value="value">
                            {{ lang_rsc }}
                        </option>
                    </template>
                </NativeSelect>

                <NativeSelect v-model:value="selected.weekday" :class="selected.monthlyMode !== modes.monthly[1].type && 'inactive'">
                    <template v-slot:options>
                        <option v-for="{ value, lang_rsc } of cronValues.weekdays" :key="selected.mode + value" :value="value">{{ lang_rsc }}</option>
                    </template>
                </NativeSelect>
            </div>

            <h3 class="h4">{{ $Resources.CronEveryLabel }}:</h3>

            <div class="form-group d-flex flex-wrap">
                <InputCheckbox
                    style="width: 33.333%"
                    v-for="({ value, lang_rsc }, idx) of cronValues.months"
                    :checked="selected.months.includes(value)"
                    :disabled="selected.monthlyMode !== modes.monthly[1].type"
                    @update:checked="(newValue) => checkboxToggle(newValue, value, selected.months)">
                    <template #label>
                        {{ lang_rsc }}
                    </template>
                </InputCheckbox>
            </div>

            <div class="form-group">
                <label>{{ $Resources.CronAtLabel }}:</label>
                <DateTimePicker type="time" v-model:value_hour="selected.hour" v-model:value_minute="selected.minute" />
            </div>
        </tab>
        <tab @tabClick="selected.mode = 'yearly'" :title="$Resources.CronYearlyTab">
            <div class="form-group d-flex gap-lg align-items-baseline flex-wrap">
                <FluidToggle
                    style="width: 100%"
                    type="radio"
                    class="slide rounded"
                    v-model:value="selected.yearlyMode"
                    :checked="modes.yearly[0].type"
                    :text="$Resources.CronEveryLabel + ':'" />
                <NativeSelect v-model:value="selected.month" class="fit-cw" :class="selected.yearlyMode !== modes.yearly[0].type && 'inactive'">
                    <template v-slot:options>
                        <option v-for="{ value, lang_rsc } of cronValues.months" :key="selected.mode + value" :value="value">
                            {{ lang_rsc }}
                        </option>
                    </template>
                </NativeSelect>

                <label>{{ $Resources.CronOnTheLabel }}:</label>

                <NativeSelect v-model:value="selected.dayOfMonth" class="fit-cw" :class="selected.yearlyMode !== modes.yearly[0].type && 'inactive'">
                    <template v-slot:options>
                        <option v-for="{ value, lang_rsc } of cronValues.daysOfMonth" :key="selected.mode + value" :value="value">
                            {{ lang_rsc }}
                        </option>
                    </template>
                </NativeSelect>
            </div>

            <div class="form-group d-flex gap-lg align-items-baseline flex-wrap">
                <FluidToggle
                    style="width: 100%"
                    type="radio"
                    class="slide rounded"
                    v-model:value="selected.yearlyMode"
                    :checked="modes.yearly[1].type"
                    :text="$Resources.CronOnTheLabel + ':'" />

                <NativeSelect v-model:value="selected.dayOrderInMonth" :class="selected.yearlyMode !== modes.yearly[1].type && 'inactive'">
                    <template v-slot:options>
                        <option v-for="{ value, lang_rsc } of cronValues.dayOrdersInMonth" :key="selected.mode + value" :value="value">
                            {{ lang_rsc }}
                        </option>
                    </template>
                </NativeSelect>

                <NativeSelect v-model:value="selected.weekday" :class="selected.yearlyMode !== modes.yearly[1].type && 'inactive'">
                    <template v-slot:options>
                        <option v-for="{ value, lang_rsc } of cronValues.weekdays" :key="selected.mode + value" :value="value">{{ lang_rsc }}</option>
                    </template>
                </NativeSelect>

                <label>{{ $Resources.CronOfLabel }}:</label>

                <NativeSelect v-model:value="selected.month" class="fit-cw" :class="selected.yearlyMode !== modes.yearly[1].type && 'inactive'">
                    <template v-slot:options>
                        <option v-for="{ value, lang_rsc } of cronValues.months" :key="selected.mode + value" :value="value">{{ lang_rsc }}</option>
                    </template>
                </NativeSelect>
            </div>

            <div class="form-group">
                <label>{{ $Resources.CronAtLabel }}:</label>
                <DateTimePicker type="time" v-model:value_hour="selected.hour" v-model:value_minute="selected.minute" />
            </div>
        </tab>
    </tabs>
    <!-- end tab content -->

    <!-- start date picker -->
    <div class="form-group">
        <label>{{ $Resources.CronStartLabel }}:</label>
        <DateTimePicker v-model:value="selected.start" />
    </div>

    <!-- timezonepicker -->
    <div class="form-group">
        <label>{{ $Resources.TimeZoneHeader }}:</label>
        <NativeSelect v-model:value="selected.timeZone">
            <template v-slot:options>
                <option v-for="timeZone of state.timeZones" :key="timeZone.TimeZoneId" :value="timeZone.TimeZoneId">{{ timeZone.Name }}</option>
            </template>
        </NativeSelect>
    </div>

    <!-- upcoming dates & times list -->
    <div class="m-lg-b" v-show="state.nextDates?.length > 0">
        <h3 class="h4">{{ $Resources.CronUpcomingDates }}:</h3>

        <ol class="list-unstyled">
            <li class="d-flex justify-content-between m-sm-b" v-for="date of state.nextDates" :key="date">
                <span>
                    {{ (weak_prop = new Date(date)).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) }}
                </span>
                <time>
                    {{ `${weak_prop.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` }}
                </time>
            </li>
        </ol>
    </div>
</template>

<style>
    .weekdays-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(4, 1fr);
        grid-auto-flow: dense;
    }

    .months-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(4, 1fr);
        grid-auto-flow: dense;
    }

    .inset-container {
        padding: 0.1rem 0.3rem;
        border-radius: 2px;
        border: 1px solid #1f1c2633;
        /* box-shadow: 0 2px 9px 0 #0000001a inset; */
    }
</style>
